/**
 * Performance Lab shared design tokens and styled atoms.
 * Used across Performance Lab page sections to reduce duplication.
 */
import type {
  CSSProperties,
  ComponentPropsWithoutRef,
  ElementType,
} from 'react';
import { forwardRef } from 'react';
import styles from './atoms.module.css';

/** Page / section background used by layout atoms (not button surfaces). */
export const PL_COLOR_BG = '#ededed';

export { PLCTA, PL_COLOR_PRIMARY, PL_COLOR_PRIMARY_HOVER } from './PLCTA';

export type { PLCTAVariant, PLCTAProps } from './PLCTA';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/* ──────────────────── Layout ──────────────────── */

export interface SectionWithBgProps extends ComponentPropsWithoutRef<'section'> {
  $fullHeight?: boolean;
  $paddingVariant?: 'default' | 'compact';
}

export const SectionWithBg = forwardRef<HTMLElement, SectionWithBgProps>(
  function SectionWithBg(
    { className, $fullHeight, $paddingVariant = 'default', ...rest },
    ref,
  ) {
    return (
      <section
        ref={ref}
        data-full-height={$fullHeight ? 'true' : undefined}
        data-padding-variant={$paddingVariant}
        className={cx(styles.sectionWithBg, className)}
        {...rest}
      />
    );
  },
);

export const SectionBg = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function SectionBg({ className, ...rest }, ref) {
  return <div ref={ref} className={cx(styles.sectionBg, className)} {...rest} />;
});

export interface SectionContentProps extends ComponentPropsWithoutRef<'div'> {
  $centered?: boolean;
}

export const SectionContent = forwardRef<HTMLDivElement, SectionContentProps>(
  function SectionContent({ className, $centered, ...rest }, ref) {
    return (
      <div
        ref={ref}
        data-centered={$centered ? 'true' : undefined}
        className={cx(styles.sectionContent, className)}
        {...rest}
      />
    );
  },
);

/* ──────────────────── Typography ──────────────────── */

export const DharmaTitleWrapper = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function DharmaTitleWrapper({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.dharmaTitleWrapper, className)}
      {...rest}
    />
  );
});

export interface DharmaHeadingProps extends ComponentPropsWithoutRef<'h2'> {
  $align?: 'left' | 'center' | 'centerMobileLeftDesktop';
  $whiteSpace?: 'pre-line' | 'pre-wrap' | 'normal';
}

export const DharmaHeading = forwardRef<HTMLHeadingElement, DharmaHeadingProps>(
  function DharmaHeading({ className, $align, $whiteSpace, ...rest }, ref) {
    return (
      <h2
        ref={ref}
        data-align={$align}
        data-white-space={$whiteSpace}
        className={cx('pl-dharma-title', styles.dharmaHeading, className)}
        {...rest}
      />
    );
  },
);

export interface BodyTextProps extends ComponentPropsWithoutRef<'p'> {
  $center?: boolean;
  $maxWidth?: string;
  /** Center on mobile, left on desktop (e.g. GetDirections) */
  $centerMobileLeftDesktop?: boolean;
  $whiteSpace?: 'pre-wrap' | 'pre-line' | 'normal';
}

export const BodyText = forwardRef<HTMLParagraphElement, BodyTextProps>(
  function BodyText(
    {
      className,
      style,
      $center,
      $maxWidth,
      $centerMobileLeftDesktop,
      $whiteSpace,
      ...rest
    },
    ref,
  ) {
    const mobileLeft = !$centerMobileLeftDesktop && $center === false;
    const desktopLeft = $center === false || Boolean($centerMobileLeftDesktop);
    return (
      <p
        ref={ref}
        data-align-mobile={mobileLeft ? 'left' : undefined}
        data-align-desktop={desktopLeft ? 'left' : undefined}
        data-white-space={$whiteSpace}
        style={
          $maxWidth
            ? ({ ...style, '--pl-body-max-width': $maxWidth } as CSSProperties)
            : style
        }
        className={cx(styles.bodyText, className)}
        {...rest}
      />
    );
  },
);

/* ──────────────────── Copy block ──────────────────── */

export const CopyBlock = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function CopyBlock({ className, ...rest }, ref) {
  return (
    <div ref={ref} className={cx(styles.copyBlock, className)} {...rest} />
  );
});

/* ──────────────────── Utilities ──────────────────── */

/** `as` preserves the styled-components polymorphic prop (e.g. `as="caption"`). */
export interface SrOnlyProps extends ComponentPropsWithoutRef<'span'> {
  as?: ElementType;
}

export const SrOnly = forwardRef<HTMLSpanElement, SrOnlyProps>(function SrOnly(
  { className, as, ...rest },
  ref,
) {
  // Runtime renders the real tag (e.g. caption); typed as 'span' for JSX props.
  const Component = (as ?? 'span') as 'span';
  return (
    <Component ref={ref} className={cx(styles.srOnly, className)} {...rest} />
  );
});
